import { BROKER_SYNC_SETTINGS_STATUS_OBJ, BROKER_SYNC_PROVIDER_OBJ } from "@signals/shared";
import { BROKER_SYNC_DEFAULT_DEEP_WINDOW_DAYS, BROKER_SYNC_DEFAULT_OVERLAP_DAYS, BROKER_SYNC_DEEP_WINDOW_DAYS_MAX, BROKER_SYNC_DEEP_WINDOW_DAYS_MIN, BROKER_SYNC_OVERLAP_DAYS_MAX, BROKER_SYNC_OVERLAP_DAYS_MIN, BROKER_SYNC_PROVIDER_LABELS, type TBrokerSyncProvider } from "@signals/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, qk, useApiMutation, useApiQuery } from "../_common";
import { t, localeKey } from "./broker-sync-panel-format.util";
import type { BrokerSyncIssuesResponse, BrokerSyncSettingsResponse, BrokerSyncSettingsUpdate, ProviderCheckResult, BrokerSyncSettingsValues } from "./broker-sync-panel.types";
import { useBrokerSyncAccounts } from "./broker-sync-status.hooks";
import { describeApiError, redactKnownSecrets } from "./broker-sync-panel-error.util";
import { AccountSyncStatus } from "./broker-sync-account-status";
import { BrokerSyncIssues } from "./broker-sync-issues";
import { NumberSetting } from "./broker-sync-number-setting";
import { ProviderSetup } from "./broker-sync-provider-setup";

const PROVIDERS: readonly {
  id: TBrokerSyncProvider;
  instructionKey: Parameters<typeof t>[0];
}[] = [
  { id: "ibkr", instructionKey: "portfolio.brokerSync.ibkrInstructions" },
  { id: "tbank", instructionKey: "portfolio.brokerSync.tbankInstructions" },
  { id: "finam", instructionKey: "portfolio.brokerSync.finamInstructions" },
];

const MANAGEMENT_HEADER = "x-mcp-management-code";

const EMPTY_SECRETS = { ibkrFlexToken: "", ibkrFlexQueryId: "", tbankToken: "", finamSecret: "" };

function managementHeaders(code: string): HeadersInit {
  return { [MANAGEMENT_HEADER]: code };
}

export function BrokerSyncPanel({ portfolioId }: { portfolioId: string }) {
  const queryClient = useQueryClient();
  const [codeInput, setCodeInput] = useState("");
  const [managementCode, setManagementCode] = useState("");
  // `null` marks a numeric field that currently holds an invalid value.
  const [draft, setDraft] = useState<{ enabled?: boolean; overlapDays?: number | null; deepWindowDays?: number | null }>({});
  const [submitBlocked, setSubmitBlocked] = useState(false);
  const [secrets, setSecrets] = useState(EMPTY_SECRETS);
  const [secretInputsKey, setSecretInputsKey] = useState(0);
  const [lastCheckedProvider, setLastCheckedProvider] = useState<TBrokerSyncProvider | null>(null);
  const [saved, setSaved] = useState(false);

  const accountsQuery = useBrokerSyncAccounts(portfolioId);
  const issuesQuery = useApiQuery<BrokerSyncIssuesResponse>(
    qk.portfolio.brokerSync.issues(portfolioId, undefined, "open"),
    `/api/portfolio/broker-sync/issues?portfolioId=${encodeURIComponent(portfolioId)}&status=open`,
    { enabled: !!portfolioId },
  );
  const settingsQuery = useApiQuery<BrokerSyncSettingsResponse>(
    qk.portfolio.brokerSync.settings,
    "/api/portfolio/broker-sync/settings",
    {
      enabled: !!managementCode,
      retry: false,
      headers: () => managementHeaders(managementCode),
    },
  );
  const saveMutation = useApiMutation<BrokerSyncSettingsUpdate, BrokerSyncSettingsResponse>(
    "/api/portfolio/broker-sync/settings",
    "PUT",
    {
      headers: () => managementHeaders(managementCode),
      invalidateKeys: [
        qk.portfolio.brokerSync.settings,
        qk.portfolio.brokerSync.allForPortfolio(portfolioId),
      ],
      gcTime: 0,
      onSuccess: () => {
        setDraft({});
        clearSecrets();
        setSaved(true);
      },
    },
  );
  const connectionMutation = useApiMutation<{ provider: TBrokerSyncProvider }, ProviderCheckResult>(
    "/api/portfolio/broker-sync/provider-accounts",
    "POST",
    { headers: () => managementHeaders(managementCode), gcTime: 0 },
  );

  function clearSecrets() {
    setSecrets(EMPTY_SECRETS);
    setSecretInputsKey((key) => key + 1);
  }

  const accounts = accountsQuery.data?.items ?? [];
  const settingsInvalidMessage = settingsQuery.data?.status === BROKER_SYNC_SETTINGS_STATUS_OBJ.INVALID ? settingsQuery.data.message : null;
  // While the stored file is unreadable the form still opens with defaults so it can be repaired.
  const settings: BrokerSyncSettingsValues | undefined = settingsQuery.data
    ? settingsQuery.data.status === BROKER_SYNC_SETTINGS_STATUS_OBJ.INVALID
      ? {
          enabled: false,
          overlapDays: BROKER_SYNC_DEFAULT_OVERLAP_DAYS,
          deepWindowDays: BROKER_SYNC_DEFAULT_DEEP_WINDOW_DAYS,
          secretStatus: {
            ibkr: { flexToken: false, flexQueryId: false },
            tbank: { token: false },
            finam: { secret: false },
          },
        }
      : settingsQuery.data
    : undefined;
  const connectionResult = connectionMutation.data;
  const knownSecrets = [managementCode, secrets.ibkrFlexToken, secrets.ibkrFlexQueryId, secrets.tbankToken, secrets.finamSecret];

  function unlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!codeInput) return;
    setSaved(false);
    if (managementCode === codeInput) {
      void settingsQuery.refetch();
    } else {
      queryClient.removeQueries({ queryKey: qk.portfolio.brokerSync.settings });
      setManagementCode(codeInput);
    }
    setCodeInput("");
  }

  function lock() {
    setManagementCode("");
    setCodeInput("");
    setDraft({});
    setSubmitBlocked(false);
    clearSecrets();
    saveMutation.reset();
    connectionMutation.reset();
    setLastCheckedProvider(null);
    queryClient.removeQueries({ queryKey: qk.portfolio.brokerSync.settings });
  }

  function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!settings) return;
    const overlapDays = draft.overlapDays === undefined ? settings.overlapDays : draft.overlapDays;
    const deepWindowDays = draft.deepWindowDays === undefined ? settings.deepWindowDays : draft.deepWindowDays;
    if (overlapDays === null || deepWindowDays === null) {
      // Never save the last valid number silently while the field shows something else.
      setSubmitBlocked(true);
      return;
    }
    setSubmitBlocked(false);
    const update: BrokerSyncSettingsUpdate = {
      enabled: draft.enabled ?? settings.enabled,
      overlapDays,
      deepWindowDays,
      ...(secrets.ibkrFlexToken || secrets.ibkrFlexQueryId
        ? { ibkr: { ...(secrets.ibkrFlexToken ? { flexToken: secrets.ibkrFlexToken } : {}), ...(secrets.ibkrFlexQueryId ? { flexQueryId: secrets.ibkrFlexQueryId } : {}) } }
        : {}),
      ...(secrets.tbankToken ? { tbank: { token: secrets.tbankToken } } : {}),
      ...(secrets.finamSecret ? { finam: { secret: secrets.finamSecret } } : {}),
    };
    setSaved(false);
    saveMutation.mutate(update);
  }

  function checkConnection(provider: TBrokerSyncProvider) {
    connectionMutation.reset();
    setLastCheckedProvider(provider);
    connectionMutation.mutate({ provider });
  }

  const settingsInvalid = accountsQuery.data?.settingsInvalid === true || settingsInvalidMessage !== null;
  const displayEnabled = settingsInvalid ? false : draft.enabled ?? settings?.enabled ?? accountsQuery.data?.enabled ?? false;
  const unlockErrorText = settingsQuery.isError ? describeApiError(settingsQuery.error, knownSecrets) : null;

  return (
    <section className="space-y-4" aria-labelledby="broker-sync-panel-title">
      <Card>
        <CardHeader>
          <CardTitle id="broker-sync-panel-title" className="text-lg">{t("portfolio.brokerSync.title")}</CardTitle>
          <CardDescription>{t("portfolio.brokerSync.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className={`text-sm font-medium ${displayEnabled ? "text-emerald-700" : "text-muted-foreground"}`}>
            {displayEnabled ? t("portfolio.brokerSync.enabled") : t("portfolio.brokerSync.disabled")}
          </p>
          {accountsQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {t("portfolio.brokerSync.statusError")} {describeApiError(accountsQuery.error, knownSecrets)}
            </p>
          ) : null}
          {settingsInvalid ? (
            <p role="alert" className="text-sm text-destructive">{t("portfolio.brokerSync.settingsFileInvalidStatus")}</p>
          ) : null}
          {accountsQuery.isLoading ? <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.statusLoading")}</p> : null}
          {!accountsQuery.isLoading && !accountsQuery.isError && accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.emptyAccounts")}</p>
          ) : null}
          {accounts.length > 0 ? (
            <div className="space-y-3">
              <h3 className="font-semibold">{t("portfolio.brokerSync.accountStatus")}</h3>
              {accounts.map((account) => <AccountSyncStatus key={account.accountId} account={account} />)}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle id="portfolio-broker-sync-issues" className="text-lg">{t("portfolio.brokerSync.issueList")}</CardTitle>
        </CardHeader>
        <CardContent>
          <BrokerSyncIssues
            issues={issuesQuery.data}
            accounts={accounts}
            errorText={issuesQuery.isError ? describeApiError(issuesQuery.error, knownSecrets) : null}
            isLoading={issuesQuery.isLoading}
            managementCode={managementCode}
            secretValues={knownSecrets}
          />
        </CardContent>
      </Card>

      {!settings ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.managementIntro")}</p>
            <details className="rounded-md border p-3">
              <summary className="cursor-pointer text-sm font-medium">{t("portfolio.brokerSync.setupInstructions")}</summary>
              <div className="mt-3 space-y-3">
                {PROVIDERS.map(({ id, instructionKey }) => (
                  <section key={id} className="space-y-1 text-sm">
                    <h3 className="font-medium">{BROKER_SYNC_PROVIDER_LABELS[id][localeKey()]}</h3>
                    <p className="text-muted-foreground">{t(instructionKey)}</p>
                  </section>
                ))}
              </div>
            </details>
            <form onSubmit={unlock} className="flex flex-wrap items-end gap-3">
              <label className="min-w-64 flex-1 space-y-1 text-sm font-medium">
                {t("portfolio.brokerSync.managementCode")}
                <input
                  type="password"
                  autoComplete="current-password"
                  value={codeInput}
                  onChange={(event) => setCodeInput(event.target.value)}
                  className="h-10 w-full rounded-md border bg-background px-3"
                  required
                />
              </label>
              <Button type="submit" disabled={settingsQuery.isFetching}>{t("portfolio.brokerSync.unlock")}</Button>
            </form>
            {settingsQuery.isFetching ? <p className="text-sm text-muted-foreground">{t("portfolio.brokerSync.checkingConnection")}</p> : null}
            {unlockErrorText ? <p role="alert" className="text-sm text-destructive">{unlockErrorText}</p> : null}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-lg">{t("portfolio.brokerSync.title")}</CardTitle>
              <Button type="button" variant="outline" onClick={lock}>{t("portfolio.brokerSync.lock")}</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <form onSubmit={saveSettings} noValidate className="space-y-4">
              {settingsInvalidMessage ? (
                <p role="alert" className="text-sm text-destructive">
                  {t("portfolio.brokerSync.settingsInvalid", { reason: redactKnownSecrets(settingsInvalidMessage, knownSecrets) })}
                </p>
              ) : null}
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={draft.enabled ?? settings.enabled}
                  onChange={(event) => { setDraft((prev) => ({ ...prev, enabled: event.target.checked })); setSaved(false); }}
                />
                {t("portfolio.brokerSync.enabled")}
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumberSetting
                  label={t("portfolio.brokerSync.overlapDays")}
                  value={draft.overlapDays === undefined ? settings.overlapDays : draft.overlapDays}
                  min={BROKER_SYNC_OVERLAP_DAYS_MIN}
                  max={BROKER_SYNC_OVERLAP_DAYS_MAX}
                  onChange={(value) => { setDraft((prev) => ({ ...prev, overlapDays: value })); setSaved(false); setSubmitBlocked(false); }}
                />
                <NumberSetting
                  label={t("portfolio.brokerSync.deepWindowDays")}
                  value={draft.deepWindowDays === undefined ? settings.deepWindowDays : draft.deepWindowDays}
                  min={BROKER_SYNC_DEEP_WINDOW_DAYS_MIN}
                  max={BROKER_SYNC_DEEP_WINDOW_DAYS_MAX}
                  onChange={(value) => { setDraft((prev) => ({ ...prev, deepWindowDays: value })); setSaved(false); setSubmitBlocked(false); }}
                />
              </div>
              <div className="space-y-3">
                {PROVIDERS.map(({ id, instructionKey }) => (
                  <ProviderSetup
                    key={id}
                    provider={id}
                    secretInputsKey={secretInputsKey}
                    instructions={t(instructionKey)}
                    managementCode={managementCode}
                    tokenStatuses={id === BROKER_SYNC_PROVIDER_OBJ.IBKR
                      ? [
                          { label: t("portfolio.brokerSync.ibkrToken"), isSet: settingsInvalidMessage ? null : settings.secretStatus.ibkr.flexToken },
                          { label: t("portfolio.brokerSync.queryId"), isSet: settingsInvalidMessage ? null : settings.secretStatus.ibkr.flexQueryId },
                        ]
                      : id === BROKER_SYNC_PROVIDER_OBJ.TBANK
                        ? [{ label: t("portfolio.brokerSync.tbankToken"), isSet: settingsInvalidMessage ? null : settings.secretStatus.tbank.token }]
                        : [{ label: t("portfolio.brokerSync.finamSecret"), isSet: settingsInvalidMessage ? null : settings.secretStatus.finam.secret }]}
                    secretValues={id === BROKER_SYNC_PROVIDER_OBJ.IBKR
                      ? [
                          { label: t("portfolio.brokerSync.ibkrToken"), hasValue: secrets.ibkrFlexToken !== "", onChange: (value) => setSecrets((prev) => ({ ...prev, ibkrFlexToken: value })) },
                          { label: t("portfolio.brokerSync.queryId"), hasValue: secrets.ibkrFlexQueryId !== "", onChange: (value) => setSecrets((prev) => ({ ...prev, ibkrFlexQueryId: value })) },
                        ]
                      : id === BROKER_SYNC_PROVIDER_OBJ.TBANK
                        ? [{ label: t("portfolio.brokerSync.tbankToken"), hasValue: secrets.tbankToken !== "", onChange: (value) => setSecrets((prev) => ({ ...prev, tbankToken: value })) }]
                        : [{ label: t("portfolio.brokerSync.finamSecret"), hasValue: secrets.finamSecret !== "", onChange: (value) => setSecrets((prev) => ({ ...prev, finamSecret: value })) }]}
                    connectionResult={connectionResult?.provider === id ? connectionResult : null}
                    connectionError={lastCheckedProvider === id && connectionMutation.isError ? describeApiError(connectionMutation.error, knownSecrets) : null}
                    checking={lastCheckedProvider === id && connectionMutation.isPending}
                    secrets={knownSecrets}
                    onCheck={checkConnection}
                  />
                ))}
              </div>
              {submitBlocked ? <p role="alert" className="text-sm text-destructive">{t("portfolio.brokerSync.numbersInvalidBlocked")}</p> : null}
              {saveMutation.isError ? <p role="alert" className="text-sm text-destructive">{describeApiError(saveMutation.error, knownSecrets)}</p> : null}
              {saved ? <p role="status" className="text-sm text-emerald-700">{t("portfolio.brokerSync.saved")}</p> : null}
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t("portfolio.brokerSync.saving") : t("portfolio.brokerSync.save")}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
